import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle, FormControl,
  Grid, InputLabel,
  Link, MenuItem, Select,
  TextField, Typography
} from '@mui/material'
import {
  type AssetHolderUpdateDto, type AssetHolderUpdateForm,
  type AssetHolderUpdatePayload, validateUpdateProfileForm
} from '../../../../api/schema-dtos/AssetHolderCrudDtos'
import { Formik, type FormikProps } from 'formik'
import { UPDATE_PROFILE_URLS } from '../../../../App'
import { DATE_FORMAT_API, formatDateStringAsApiFormat } from '../../../../utility/DateFormatter'
import ErrorComponent from '../../../shared/components/ui-widgets/ErrorComponent'
import OtpComponent from '../../../auth/components/OtpComponent'
import { updateAssetHolderDetailsMutation } from '../../../../api/asset-holders/assetHolderApi'
import { useAppDispatch } from '../../../../app/hooks'
import { setNotification } from '../../../shared/components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../../api/shared/RtkQueryHelper'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { isSameDay } from 'date-fns'
import { type CountryListItem, getCountryOptions } from '../../../../utility/GeneralUtils'

interface UpdateProfileComponentProps {
  assetHolderDetails: AssetHolderUpdateDto
}

const UpdateProfileComponent: React.FC<UpdateProfileComponentProps> = (props: UpdateProfileComponentProps) => {
  const { assetHolderDetails } = props

  const dispatch = useAppDispatch()
  const [openDialog, toggleDialog] = useState<boolean>(false)
  const [otp, setOtp] = useState<string>('')

  const [apiUpdateAssetHolder, { isLoading }] = updateAssetHolderDetailsMutation()

  const defaultForm: AssetHolderUpdateForm = {
    legacyFirstName: assetHolderDetails.legacyFirstName,
    legacyLastName: assetHolderDetails.legacyLastName,
    legacyDob: assetHolderDetails.legacyDob ?? '',
    legacyPhoneNumber: assetHolderDetails.legacyPhoneNumber,
    legacyAddress: assetHolderDetails.legacyAddress ?? '',
    legacyCity: assetHolderDetails.legacyCity ?? '',
    legacyPostalCode: assetHolderDetails.legacyPostalCode ?? '',
    legacyRegion: assetHolderDetails.legacyRegion ?? '',
    legacyCountry: assetHolderDetails.legacyCountry ?? '',
    otp: '',
    unethicalProfitAccepted: false,
    acceptTermsAndConditionsAndPrivacyPolicy: false,
    agreeToMarketing: assetHolderDetails.agreeToMarketing,
    isUsCitizen: assetHolderDetails.usCitizenStatus.isUsCitizen ?? false
  }

  const resetKycFieldNames = ['legacyFirstName', 'legacyLastName', 'legacyDob', 'legacyCountry', 'isUsCitizen']

  const hasKycResetFieldsChanges = (formikProps: FormikProps<AssetHolderUpdateForm>): boolean => {
    let resetKyc: boolean = false

    for (const key of Object.keys(formikProps.values)) {
      if (resetKycFieldNames.includes(key)) {
        if (key === 'legacyDob') {
          if (!isSameDay((assetHolderDetails.legacyDob !== null ? new Date(assetHolderDetails.legacyDob) : new Date()), new Date(formikProps.values.legacyDob))) {
            resetKyc = true
          }
        } else if (key === 'isUsCitizen') {
          console.log(`${key} => ${assetHolderDetails.usCitizenStatus.isUsCitizen} / ${formikProps.values.isUsCitizen}`)
          if ((assetHolderDetails.usCitizenStatus.isUsCitizen === undefined && formikProps.values.isUsCitizen) ||
            (assetHolderDetails.usCitizenStatus.isUsCitizen !== undefined && assetHolderDetails.usCitizenStatus.isUsCitizen !== formikProps.values.isUsCitizen)) {
            resetKyc = true
          }
        } else {
          const currentValue = (assetHolderDetails as any)[key]
          const formValue = (formikProps.values as any)[key]
          if (currentValue !== formValue) {
            console.log(`${key} => ${currentValue} !== ${formValue}`)
            resetKyc = true
          }
        }
      }
    }

    return resetKyc
  }

  const processFormDataOnSubmit = (values: AssetHolderUpdateForm): void => {
    const payload: AssetHolderUpdatePayload = {
      legacyFirstName: values.legacyFirstName,
      legacyLastName: values.legacyLastName,
      legacyDob: formatDateStringAsApiFormat(values.legacyDob) ?? '',
      legacyPhoneNumber: values.legacyPhoneNumber,
      legacyAddress: values.legacyAddress,
      legacyCity: values.legacyCity,
      legacyPostalCode: values.legacyPostalCode,
      legacyRegion: values.legacyRegion,
      legacyCountry: values.legacyCountry,
      otp,
      agreeToMarketing: values.agreeToMarketing,
      isUsCitizen: values.isUsCitizen
    }

    apiUpdateAssetHolder({
      assetHolderId: assetHolderDetails.assetHolderId,
      body: { ...payload, otp }
    })
      .unwrap()
      .then(() => {
        dispatch(setNotification({ open: true, severity: 'success', message: 'The profile has been updated' }))
        toggleDialog(false)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  const formatError = (formikProps: FormikProps<AssetHolderUpdateForm>): string => {
    return `You cannot update your profile until the following issues have been resolved: ${Object.values(formikProps.errors).join(', ')}`
  }

  return (
    <>
      <Button
        variant={'contained'}
        color={'primary'}
        sx={{ mr: 2 }}
        onClick={() => {
          toggleDialog(true)
        }}
      >
        Edit Profile
      </Button>

      <Dialog
        open={openDialog}
        maxWidth={'xl'}
        fullWidth={true}
        disableEscapeKeyDown={true}
        onClose={() => {
          toggleDialog(false)
        }}
        sx={{ p: 2 }}
      >
        <DialogTitle variant={'h3'}>Edit Profile</DialogTitle>

        <DialogContent>
          <Formik
            initialValues={defaultForm}
            validateOnMount={true}
            validationSchema={validateUpdateProfileForm}
            onSubmit={processFormDataOnSubmit }
          >
            {formikProps => (
              <form>
                <Grid container spacing={3}>
                  {!(formikProps.isValid) && (
                    <Grid item md={12} sx={{ mt: 3 }}>
                      <ErrorComponent title={'Form validation error'}
                                      message={formatError(formikProps)}/>
                    </Grid>
                  )}
                  <Grid item md={6} xs={12} sx={{ mt: 2 }}>
                    <TextField
                      label={'First Name'}
                      type={'text'}
                      variant="outlined"
                      sx={{ width: '100%' }}
                      placeholder={'First Name'}

                      {...formikProps.getFieldProps('legacyFirstName')}

                    />
                  </Grid>
                  <Grid item md={6} xs={12} sx={{ mt: 2 }}>
                    <TextField
                      label={'Last Name'}
                      type={'text'}
                      variant="outlined"
                      sx={{ width: '100%' }}
                      placeholder={'Last Name'}

                      {...formikProps.getFieldProps('legacyLastName')}

                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Date of Birth"
                        inputFormat={DATE_FORMAT_API}
                        disableMaskedInput={true}
                        onChange={(newValue: any) => {
                          void formikProps.setFieldValue('legacyDob', newValue)
                        }}
                        value={formikProps.values.legacyDob}
                        renderInput={(params: any) => <TextField fullWidth {...params} />}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      label={'Phone Number'}
                      type={'text'}
                      variant="outlined"
                      sx={{ width: '100%' }}
                      placeholder={'Phone Number'}

                      {...formikProps.getFieldProps('legacyPhoneNumber')}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      label={'Address'}
                      type={'text'}
                      variant="outlined"
                      sx={{ width: '100%' }}
                      placeholder={'Phone Number'}

                      {...formikProps.getFieldProps('legacyAddress')}
                    />
                  </Grid>

                  <Grid item md={6} xs={12}>
                    <TextField
                      label={'City'}
                      type={'text'}
                      variant="outlined"
                      sx={{ width: '100%' }}
                      placeholder={'Phone Number'}

                      {...formikProps.getFieldProps('legacyCity')}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      label={'Postal Code'}
                      type={'text'}
                      variant="outlined"
                      sx={{ width: '100%' }}
                      placeholder={'Phone Number'}

                      {...formikProps.getFieldProps('legacyPostalCode')}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      label={'State / Province / Region'}
                      type={'text'}
                      variant="outlined"
                      sx={{ width: '100%' }}
                      placeholder={'State / Province / Region'}

                      {...formikProps.getFieldProps('legacyRegion')}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Country</InputLabel>
                      <Select
                        {...formikProps.getFieldProps('legacyCountry')}
                        label="Country"
                        onChange={async event => {
                          await formikProps.setFieldValue('legacyCountry', event.target.value)
                        }}
                      >
                        {getCountryOptions(true).map((country: CountryListItem, index: number) => (
                          <MenuItem key={index} value={country.value}>{country.text}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {hasKycResetFieldsChanges(formikProps) &&
                    <Grid item md={12}>
                      <Alert severity="warning">
                        <b>Note:</b> Changing your first name, last name, date of birth, country or US citizen status
                        will require you to verify
                        your identity.
                      </Alert>
                    </Grid>
                  }

                  <Grid item md={12}>
                    <Box style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}>
                      <Checkbox checked={formikProps.values.acceptTermsAndConditionsAndPrivacyPolicy}
                                onChange={async () => await formikProps.setFieldValue('acceptTermsAndConditionsAndPrivacyPolicy', !formikProps.values.acceptTermsAndConditionsAndPrivacyPolicy)}/>
                      <Typography style={{ marginLeft: 10 }} variant={'body1'}>
                        I agree with the Sun Exchange&#39;s <Link target="_blank" href={UPDATE_PROFILE_URLS[0].url}> terms and
                        conditions</Link> and <Link target="_blank" href={UPDATE_PROFILE_URLS[1].url}>privacy policy</Link>.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item md={12}>
                    <Box style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}>
                      <Checkbox checked={formikProps.values.agreeToMarketing}
                                onChange={async () => await formikProps.setFieldValue('agreeToMarketing', !formikProps.values.agreeToMarketing)}/>
                      <Typography style={{ marginLeft: 10 }} variant={'body1'}>
                        I consent to be emailed: account activity, my solar project updates, important news. Unsubscribe
                        at any time.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item md={12}>
                    <Box style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}>
                      <Checkbox checked={formikProps.values.unethicalProfitAccepted}
                                onChange={async () => await formikProps.setFieldValue('unethicalProfitAccepted', !formikProps.values.unethicalProfitAccepted)}/>
                      <Typography style={{ marginLeft: 10 }} variant={'body1'}>
                        Please confirm that you do not make money from any of the <Link target="_blank" href={UPDATE_PROFILE_URLS[2].url}>member prohibitions</Link>.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item md={12}>
                    <Box style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}>
                      <Checkbox checked={formikProps.values.isUsCitizen}
                                onChange={async () => await formikProps.setFieldValue('isUsCitizen', !formikProps.values.isUsCitizen)}/>
                      <Typography style={{ marginLeft: 10 }} variant={'body1'}>
                        Are you a U.S. citizen as <a
                        href="https://www.ecfr.gov/current/title-17/chapter-II/part-230#p-230.902(k)" target="_blank"
                        rel="noreferrer">defined by the Securities and Exchange Commission</a>?
                      </Typography>
                    </Box>
                  </Grid>

                  {/* {formikProps.values.isUsCitizen && */}
                  {/*  <Grid item md={12}> */}
                  {/*    <Alert severity="warning"> */}
                  {/*      Apologies, US friends! Current regulations prohibit your access. Subscribe to our newsletter for */}
                  {/*      updates! */}
                  {/*    </Alert> */}
                  {/*  </Grid> */}
                  {/* } */}

                  <Grid item md={12}>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <OtpComponent
                        assetHolderId={assetHolderDetails.assetHolderId}
                        actionName={'Update Profile'}
                        isLoading={isLoading}
                        disabled={!formikProps.isValid}
                        authenticatorConfigured={assetHolderDetails.twoFAStatus}
                        executeAction={async (otp: string) => {
                          setOtp(otp)
                          await formikProps.submitForm()
                        }}
                      />

                      <Button
                        variant={'outlined'}
                        type="button"
                        sx={{ ml: 2 }}
                        onClick={() => {
                          toggleDialog(false)
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default UpdateProfileComponent
